import React, { useState, useEffect } from 'react';
import { Users, Shield, Bell, Check, UserPlus, ShieldCheck, Lock, Mail, Plug, Activity, Trash2, KeyRound } from 'lucide-react';
import { usersApi, type ManagedUser } from '../api/usersApi';
import { useAuth } from '../context/AuthContext';
import { usePortCo } from '../context/PortCoContext';
import { IntegrationHub } from './IntegrationHub';
import { AgentFleetPage } from './AgentFleetPage';

type SectionTab = 'team' | 'platform' | 'integrations' | 'agent-fleet' | 'notifications';

function Toggle({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
        enabled ? 'bg-mantis-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

export const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SectionTab>('team');
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // 1. User Management (live, per-tenant, seat-limited)
  const { user: me } = useAuth();
  const { selectedPortco, portcos } = usePortCo();
  const isPortfolio = me?.role === 'portfolio_admin';

  const [teamUsers, setTeamUsers] = useState<ManagedUser[]>([]);
  const [seatLimit, setSeatLimit] = useState(5);
  const [usersLoading, setUsersLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ name: string; password: string; role: string; tenants: string[] }>({ name: '', password: '', role: 'tenant_admin', tenants: [] });
  const [editError, setEditError] = useState<string | null>(null);

  // defaults for invite form depend on who's inviting
  const defaultInvite = () => ({
    name: '',
    email: '',
    password: '',
    role: 'tenant_admin' as string,
    tenants: isPortfolio ? (selectedPortco !== 'all' ? [selectedPortco] : portcos.slice(0, 1).map((p: any) => p.id)) : [...(me?.tenants ?? [])],
  });
  const [newUser, setNewUser] = useState(defaultInvite());

  const reloadUsers = async () => {
    setUsersLoading(true);
    try {
      const { users, seatLimit: limit } = await usersApi.list(selectedPortco);
      setTeamUsers(users);
      setSeatLimit(limit);
    } catch (e: any) {
      showSaveToast(e?.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => { reloadUsers(); }, [selectedPortco]);
  useEffect(() => { setNewUser(defaultInvite()); }, [selectedPortco, isPortfolio]);

  const tenantUsers = (tenantId: string) => teamUsers.filter((u) => u.tenants.includes(tenantId));
  const seatLabel = selectedPortco !== 'all'
    ? `${tenantUsers(selectedPortco).length}/${seatLimit} seats`
    : `${teamUsers.length} users`;

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    if (!newUser.name || !newUser.email || !newUser.password) return;
    setInviteSubmitting(true);
    try {
      await usersApi.create({
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: isPortfolio ? newUser.role : 'tenant_admin',
        tenants: newUser.tenants.filter(Boolean),
      });
      setNewUser(defaultInvite());
      setShowInviteForm(false);
      await reloadUsers();
      showSaveToast('User created.');
    } catch (e: any) {
      setInviteError(e?.message || 'Failed to create user');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleSaveEdit = async (u: ManagedUser) => {
    setEditError(null);
    try {
      await usersApi.update(u.id, {
        name: editForm.name || undefined,
        password: editForm.password || undefined,
        ...(isPortfolio ? { role: editForm.role, tenants: editForm.tenants.filter(Boolean) } : {}),
      });
      setEditingId(null);
      await reloadUsers();
      showSaveToast('User updated.');
    } catch (e: any) {
      setEditError(e?.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (u: ManagedUser) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? They will immediately lose access.`)) return;
    try {
      await usersApi.remove(u.id);
      await reloadUsers();
      showSaveToast('User deleted.');
    } catch (e: any) {
      showSaveToast(e?.message || 'Failed to delete user');
    }
  };

  // 2. Platform Settings
  const [platformSettings, setPlatformSettings] = useState({
    hipaaCompliant: true,
    openAiRetention: 'zero_retention',
    auditRetentionYears: '7',
    autoApproveLowRisk: true,
  });

  // 3. Notifications
  const [notifications, setNotifications] = useState({
    emailDigest: 'daily',
    highRiskAlerts: true,
    weeklyReport: true,
  });

  const showSaveToast = (msg: string) => {
    setSavedMessage(msg);
    setTimeout(() => setSavedMessage(null), 3000);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 mb-1">Settings</h1>
          <p className="text-xs text-slate-500">Manage team members, HIPAA & AI platform security, and notification preferences</p>
        </div>
        {savedMessage && (
          <div className="flex items-center space-x-2 bg-mantis-50 border border-mantis-200 text-mantis-700 text-xs px-3 py-1.5 rounded-lg">
            <Check className="w-3.5 h-3.5" />
            <span>{savedMessage}</span>
          </div>
        )}
      </div>

      {/* Simplified 3-Tab Navigation */}
      <div className="flex items-center space-x-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('team')}
          className={`flex items-center space-x-2 px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
            activeTab === 'team'
              ? 'bg-mantis-50 text-mantis-600 border border-mantis-200'
              : 'text-slate-500 hover:text-slate-800 hover:bg-white'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>User Management ({seatLabel})</span>
        </button>

        <button
          onClick={() => setActiveTab('platform')}
          className={`flex items-center space-x-2 px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
            activeTab === 'platform'
              ? 'bg-mantis-50 text-mantis-600 border border-mantis-200'
              : 'text-slate-500 hover:text-slate-800 hover:bg-white'
          }`}
        >
          <Shield className="w-4 h-4" />
          <span>Platform Settings</span>
        </button>

        <button
          onClick={() => setActiveTab('integrations')}
          className={`flex items-center space-x-2 px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
            activeTab === 'integrations'
              ? 'bg-mantis-50 text-mantis-600 border border-mantis-200'
              : 'text-slate-500 hover:text-slate-800 hover:bg-white'
          }`}
        >
          <Plug className="w-4 h-4" />
          <span>Integrations</span>
        </button>

        <button
          onClick={() => setActiveTab('agent-fleet')}
          className={`flex items-center space-x-2 px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
            activeTab === 'agent-fleet'
              ? 'bg-mantis-50 text-mantis-600 border border-mantis-200'
              : 'text-slate-500 hover:text-slate-800 hover:bg-white'
          }`}
        >
          <Activity className="w-4 h-4" />
          <span>Agent Fleet</span>
        </button>

        <button
          onClick={() => setActiveTab('notifications')}
          className={`flex items-center space-x-2 px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
            activeTab === 'notifications'
              ? 'bg-mantis-50 text-mantis-600 border border-mantis-200'
              : 'text-slate-500 hover:text-slate-800 hover:bg-white'
          }`}
        >
          <Bell className="w-4 h-4" />
          <span>Notifications</span>
        </button>
      </div>

      {/* Tab 1: Team Members */}
      {activeTab === 'team' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-slate-800">User Management</h2>
              <p className="text-xs text-slate-500">
                {isPortfolio
                  ? 'All users across your portfolio — manage admins per business'
                  : `Users with access to ${selectedPortco !== 'all' ? selectedPortco.replace('portco_', '') : 'your businesses'} (${seatLimit} seats per business)`}
              </p>
            </div>
            <button
              onClick={() => { setShowInviteForm((v) => !v); setInviteError(null); }}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-semibold bg-mantis-600 hover:bg-mantis-500 text-white rounded-lg transition-colors"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Add User</span>
            </button>
          </div>

          {/* Seat usage */}
          <div className="flex items-center gap-3 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <ShieldCheck className="w-3.5 h-3.5 text-mantis-600 shrink-0" />
            {selectedPortco !== 'all' ? (
              <span><b className="text-slate-700">{tenantUsers(selectedPortco).length}</b> of <b className="text-slate-700">{seatLimit}</b> seats used for this business</span>
            ) : (
              <span>Per-business seat usage: {portcos.map((p: any) => `${p.name.replace(/ .*/, '')} ${tenantUsers(p.id).length}/${seatLimit}`).join(' · ')}</span>
            )}
          </div>

          {/* Invite Form */}
          {showInviteForm && (
            <form onSubmit={handleAddUser} className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
              <h3 className="text-xs font-semibold text-slate-800">Add New User</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <input
                  type="text" placeholder="Full Name" value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required
                  className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-mantis-500"
                />
                <input
                  type="email" placeholder="Email Address" value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required
                  className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-mantis-500"
                />
                <input
                  type="password" placeholder="Password (min 8 chars)" value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required minLength={8}
                  className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-mantis-500"
                />
                {isPortfolio ? (
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-mantis-500"
                  >
                    <option value="tenant_admin">Business Admin</option>
                    <option value="portfolio_admin">Portfolio Admin</option>
                  </select>
                ) : (
                  <div className="flex items-center text-[11px] text-slate-500 px-1">Business Admin</div>
                )}
              </div>
              {isPortfolio && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] text-slate-500">Assign to businesses:</span>
                  {portcos.map((p: any) => (
                    <label key={p.id} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1 text-[11px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newUser.tenants.includes(p.id)}
                        onChange={(e) => setNewUser({
                          ...newUser,
                          tenants: e.target.checked ? [...newUser.tenants, p.id] : newUser.tenants.filter((t: string) => t !== p.id),
                        })}
                      />
                      {p.name}
                    </label>
                  ))}
                  {newUser.role === 'portfolio_admin' && <span className="text-[10px] text-slate-400">(portfolio admins get access to everything)</span>}
                </div>
              )}
              {inviteError && <p className="text-[11px] text-red-500">{inviteError}</p>}
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={() => { setShowInviteForm(false); setInviteError(null); }}
                  className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800">Cancel</button>
                <button type="submit" disabled={inviteSubmitting}
                  className="px-3 py-1.5 text-xs font-semibold bg-mantis-600 hover:bg-mantis-500 disabled:opacity-50 text-white rounded-lg transition-colors">
                  {inviteSubmitting ? 'Creating…' : 'Create User'}
                </button>
              </div>
            </form>
          )}

          {/* Users Table */}
          {usersLoading ? (
            <p className="text-xs text-slate-400 py-6 text-center">Loading users…</p>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-2.5 px-3">User</th>
                  <th className="py-2.5 px-3">Role</th>
                  <th className="py-2.5 px-3">Businesses</th>
                  <th className="py-2.5 px-3 text-right">Manage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {teamUsers.map((u) => {
                  const isMe = u.id === me?.id;
                  const editing = editingId === u.id;
                  return (
                    <tr key={u.id} className={editing ? 'bg-mantis-50/40' : 'hover:bg-slate-50 transition-colors'}>
                      <td className="py-3 px-3">
                        {editing ? (
                          <input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs w-full focus:outline-none focus:border-mantis-500"
                          />
                        ) : (
                          <>
                            <div className="font-semibold text-slate-800">
                              {u.name} {isMe && <span className="text-[10px] font-normal text-mantis-600">(you)</span>}
                            </div>
                            <div className="text-slate-500 text-[11px]">{u.email}</div>
                          </>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        {editing && isPortfolio ? (
                          <select
                            value={editForm.role}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                            className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-mantis-500"
                          >
                            <option value="tenant_admin">Business Admin</option>
                            <option value="portfolio_admin">Portfolio Admin</option>
                          </select>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-mantis-50 text-mantis-600 border-mantis-200">
                              {u.role === 'portfolio_admin' ? 'PORTFOLIO ADMIN' : 'BUSINESS ADMIN'}
                            </span>
                            {u.firmId && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-sky-50 text-sky-700 border-sky-200" title={`Firm admin — access follows their firm's businesses`}>
                                FIRM ADMIN
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        {editing && isPortfolio && u.role !== 'portfolio_admin' ? (
                          <div className="flex flex-wrap gap-1">
                            {portcos.map((p: any) => (
                              <label key={p.id} className="inline-flex items-center gap-1 text-[10px] cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editForm.tenants.includes(p.id)}
                                  onChange={(e) => setEditForm({
                                    ...editForm,
                                    tenants: e.target.checked ? [...editForm.tenants, p.id] : editForm.tenants.filter((t: string) => t !== p.id),
                                  })}
                                />
                                {p.name.replace(/ .*/, '')}
                              </label>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-600">
                            {u.tenants.includes('*')
                              ? 'All businesses'
                              : u.firmId && u.tenants.length === 0
                              ? <em className="text-slate-400">their firm's businesses (auto)</em>
                              : u.tenants.map((t) => t.replace('portco_', '').replace(/_/g, ' ')).join(', ')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {editing ? (
                            <>
                              <input
                                type="password" placeholder="New password (optional)" value={editForm.password}
                                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} minLength={8}
                                className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-[11px] w-40 focus:outline-none focus:border-mantis-500"
                              />
                              <button onClick={() => handleSaveEdit(u)}
                                className="px-2 py-1 text-[11px] font-semibold bg-mantis-600 hover:bg-mantis-500 text-white rounded-lg">Save</button>
                              <button onClick={() => { setEditingId(null); setEditError(null); }}
                                className="px-2 py-1 text-[11px] font-medium text-slate-500 hover:text-slate-800">Cancel</button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setEditingId(u.id);
                                  setEditError(null);
                                  setEditForm({ name: u.name, password: '', role: u.role, tenants: [...u.tenants.filter((t) => t !== '*')] });
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate-600 hover:text-mantis-600 border border-slate-200 rounded-lg"
                                title="Edit user"
                              >
                                <KeyRound className="w-3 h-3" /> Edit
                              </button>
                              {!isMe && (
                                <button
                                  onClick={() => handleDeleteUser(u)}
                                  className="inline-flex items-center px-2 py-1 text-[11px] font-medium text-slate-600 hover:text-red-600 border border-slate-200 rounded-lg"
                                  title="Delete user"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {teamUsers.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-slate-400 text-xs">No users found</td></tr>
                )}
              </tbody>
            </table>
            {editError && <p className="text-[11px] text-red-500 mt-2">{editError}</p>}
          </div>
          )}
        </div>
      )}

      {/* Tab 2: Platform Settings (HIPAA & OpenAI Retention) */}
      {activeTab === 'platform' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6 shadow-sm">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Platform Settings</h2>
            <p className="text-xs text-slate-500">HIPAA compliance, AI data retention policies, and security controls</p>
          </div>

          <div className="space-y-4 divide-y divide-slate-100">
            {/* HIPAA Compliance */}
            <div className="pt-2 flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-xs font-semibold text-slate-800 flex items-center space-x-1.5">
                  <ShieldCheck className="w-4 h-4 text-mantis-600" />
                  <span>HIPAA Compliance Mode</span>
                </div>
                <p className="text-[11px] text-slate-500">Ensure PHI redactors process all agent prompts and clinical payloads</p>
              </div>
              <Toggle
                enabled={platformSettings.hipaaCompliant}
                onChange={() => {
                  setPlatformSettings(p => ({ ...p, hipaaCompliant: !p.hipaaCompliant }));
                  showSaveToast('HIPAA compliance setting updated');
                }}
              />
            </div>

            {/* OpenAI Data Retention */}
            <div className="pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="text-xs font-semibold text-slate-800 flex items-center space-x-1.5">
                  <Lock className="w-4 h-4 text-slate-600" />
                  <span>OpenAI Retention Policy</span>
                </div>
                <p className="text-[11px] text-slate-500">Configure zero data retention agreement under Enterprise BAA</p>
              </div>
              <select
                value={platformSettings.openAiRetention}
                onChange={(e) => {
                  setPlatformSettings(p => ({ ...p, openAiRetention: e.target.value }));
                  showSaveToast('OpenAI retention policy saved');
                }}
                className="bg-white border border-slate-200 text-xs text-slate-800 rounded-lg px-3 py-1.5 focus:outline-none focus:border-mantis-500"
              >
                <option value="zero_retention">Zero Data Retention (ZDR)</option>
                <option value="30_days">30-Day Audit Window</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            {/* Audit Retention */}
            <div className="pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="text-xs font-semibold text-slate-800">Audit Log Retention Period</div>
                <p className="text-[11px] text-slate-500">Storage duration for immutable governance and action logs</p>
              </div>
              <select
                value={platformSettings.auditRetentionYears}
                onChange={(e) => {
                  setPlatformSettings(p => ({ ...p, auditRetentionYears: e.target.value }));
                  showSaveToast('Audit retention updated');
                }}
                className="bg-white border border-slate-200 text-xs text-slate-800 rounded-lg px-3 py-1.5 focus:outline-none focus:border-mantis-500"
              >
                <option value="5">5 Years</option>
                <option value="7">7 Years (Medical Record Standard)</option>
                <option value="10">10 Years</option>
              </select>
            </div>

            {/* Auto Approve Low Risk */}
            <div className="pt-4 flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-xs font-semibold text-slate-800">Auto-Approve Low Risk Actions</div>
                <p className="text-[11px] text-slate-500">Automatically approve routine, non-clinical tasks</p>
              </div>
              <Toggle
                enabled={platformSettings.autoApproveLowRisk}
                onChange={() => {
                  setPlatformSettings(p => ({ ...p, autoApproveLowRisk: !p.autoApproveLowRisk }));
                  showSaveToast('Auto-approve setting updated');
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Notifications */}
      {activeTab === 'integrations' && (
        <div>
          <IntegrationHub />
        </div>
      )}

      {activeTab === 'agent-fleet' && (
        <div>
          <AgentFleetPage />
        </div>
      )}

      {activeTab === 'notifications' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6 shadow-sm">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Notification Preferences</h2>
            <p className="text-xs text-slate-500">Configure digest emails and instant alerts for human approvals</p>
          </div>

          <div className="space-y-4 divide-y divide-slate-100">
            {/* Email Digest */}
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="space-y-0.5">
                <div className="text-xs font-semibold text-slate-800 flex items-center space-x-1.5">
                  <Mail className="w-4 h-4 text-slate-600" />
                  <span>Email Summary Digest</span>
                </div>
                <p className="text-[11px] text-slate-500">Frequency of pending approval summary emails</p>
              </div>
              <select
                value={notifications.emailDigest}
                onChange={(e) => {
                  setNotifications(n => ({ ...n, emailDigest: e.target.value }));
                  showSaveToast('Email digest preference saved');
                }}
                className="bg-white border border-slate-200 text-xs text-slate-800 rounded-lg px-3 py-1.5 focus:outline-none focus:border-mantis-500"
              >
                <option value="realtime">Real-time alerts</option>
                <option value="daily">Daily Summary</option>
                <option value="weekly">Weekly Digest</option>
                <option value="off">Disabled</option>
              </select>
            </div>

            {/* High Risk Alerts */}
            <div className="pt-4 flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-xs font-semibold text-slate-800">High-Risk Instant Alerts</div>
                <p className="text-[11px] text-slate-500">Notify reviewers immediately when high-risk actions are proposed</p>
              </div>
              <Toggle
                enabled={notifications.highRiskAlerts}
                onChange={() => {
                  setNotifications(n => ({ ...n, highRiskAlerts: !n.highRiskAlerts }));
                  showSaveToast('High-risk alert setting updated');
                }}
              />
            </div>

            {/* Weekly Governance Report */}
            <div className="pt-4 flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="text-xs font-semibold text-slate-800">Weekly Executive Report</div>
                <p className="text-[11px] text-slate-500">Send automated weekly governance summary report</p>
              </div>
              <Toggle
                enabled={notifications.weeklyReport}
                onChange={() => {
                  setNotifications(n => ({ ...n, weeklyReport: !n.weeklyReport }));
                  showSaveToast('Weekly report setting updated');
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
